import axios from 'axios'
import { apiClient, handleApiRequest } from './client'

const API_BASE_URL = import.meta.env.VITE_API_URL || ''

const publicClient = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
})

export const SYSTEM_BRANDING_PUBLIC_KEY = ['system-branding-public'] as const
export const STITUFY_HOME_URL = 'https://stitufy.ch'
export const MAX_SYSTEM_LOGO_BYTES = 300 * 1024
const ALLOWED_LOGO_TYPES = new Set(['image/png', 'image/jpeg', 'image/svg+xml'])

export interface SystemBrandingAdmin {
  email_logo_original_data: string | null
  email_logo_original_mime: string | null
  email_logo_png_data: string | null
  has_email_logo: boolean
  sidebar_logo_original_data: string | null
  sidebar_logo_original_mime: string | null
  sidebar_logo_png_data: string | null
  has_sidebar_logo: boolean
  powered_by_url: string | null
}

export interface SystemBrandingPublic {
  logo_svg: string | null
  logo_png: string | null
  email_logo_svg: string | null
  email_logo_png: string | null
  url: string | null
}

export function isSafeHttpUrl(raw: string | null | undefined): boolean {
  if (!raw || typeof raw !== 'string') return false
  const value = raw.trim()
  if (!value || value.length > 500) return false
  try {
    const parsed = new URL(value)
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
  } catch {
    return false
  }
}

export function poweredByHref(raw: string | null | undefined): string {
  if (!raw || typeof raw !== 'string') return STITUFY_HOME_URL
  const value = raw.trim()
  return isSafeHttpUrl(value) ? value : STITUFY_HOME_URL
}

export function logoFileError(file: File): string | null {
  if (file.size > MAX_SYSTEM_LOGO_BYTES) {
    return `Datei zu gross. Maximum ${MAX_SYSTEM_LOGO_BYTES / 1024} KB.`
  }
  const mime = ((file.type || '').split(';')[0] || '').trim().toLowerCase()
  const name = file.name.toLowerCase()
  const extOk = name.endsWith('.png') || name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.svg')
  if (mime && !ALLOWED_LOGO_TYPES.has(mime === 'image/jpg' ? 'image/jpeg' : mime) && !extOk) {
    return 'Ungültiger Dateityp. Erlaubt: PNG, JPEG, SVG.'
  }
  if (!mime && !extOk) {
    return 'Ungültiger Dateityp. Erlaubt: PNG, JPEG, SVG.'
  }
  return null
}

export const getPublicSystemBranding = async (): Promise<SystemBrandingPublic> => {
  const { data } = await publicClient.get<SystemBrandingPublic>('/api/v1/system-branding/public')
  return data
}

export const systemBrandingApi = {
  get: () =>
    handleApiRequest<SystemBrandingAdmin>(apiClient.get('/api/v1/ultraadmin/system-branding')),

  update: (opts: { emailFile?: File; sidebarFile?: File; poweredByUrl?: string }) => {
    const formData = new FormData()
    if (opts.emailFile) formData.append('email_file', opts.emailFile)
    if (opts.sidebarFile) formData.append('sidebar_file', opts.sidebarFile)
    if (opts.poweredByUrl !== undefined) formData.append('powered_by_url', opts.poweredByUrl)
    return handleApiRequest<SystemBrandingAdmin>(
      apiClient.put('/api/v1/ultraadmin/system-branding', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
    )
  },

  removeEmailLogo: () =>
    handleApiRequest<SystemBrandingAdmin>(apiClient.delete('/api/v1/ultraadmin/system-branding/logo/email')),

  removeSidebarLogo: () =>
    handleApiRequest<SystemBrandingAdmin>(apiClient.delete('/api/v1/ultraadmin/system-branding/logo/sidebar')),
}
